import { Link } from "@remix-run/react";
import { localePath } from "~/utils/locale";
import { t } from "~/utils/translations";
import type { Locale } from "~/utils/locale";
import { useNonce } from "~/utils/nonce-provider";

interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface BreadcrumbsProps {
  locale: Locale;
  items: BreadcrumbItem[];
  origin?: string;
}

/**
 * Reusable breadcrumb navigation for storefront inner pages.
 * Last item is rendered as plain text (current page).
 */
export function Breadcrumbs({ locale, items, origin }: BreadcrumbsProps) {
  const nonce = useNonce();
  // M-25 FIX: Use dynamic origin instead of hardcoded domain
  const siteOrigin = origin || "https://diolo.com";
  const allItems: BreadcrumbItem[] = [
    { label: t("home", locale), to: "/" },
    ...items,
  ];

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": allItems.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.label,
      "item": item.to
        ? `${siteOrigin}${localePath(item.to, locale)}`
        : undefined,
    })),
  };

  return (
    <>
      <nav aria-label="Breadcrumb" className="mb-3">
        <ol className="flex items-center gap-2 text-xs font-normal text-[#767676]">
          {allItems.map((item, i) => (
            <li key={i} className="flex items-center gap-2">
              {i > 0 && (
                <span className="text-[#c0c0c0]" aria-hidden="true">/</span>
              )}
              {item.to && i < allItems.length - 1 ? (
                <Link
                  to={localePath(item.to, locale)}
                  className="hover:text-black transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="text-black" aria-current="page">
                  {item.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>
      <script
        type="application/ld+json"
        nonce={nonce}
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
    </>
  );
}
